import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Loader2, Check, Pencil, X, Copy, RefreshCw, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const STRATEGIES = [
  { id: 'benefit', label: 'Język korzyści', description: 'Podkreśl wartość i profity' },
  { id: 'fear', label: 'Strach przed stratą', description: 'FOMO - co tracisz nie działając' },
  { id: 'question', label: 'Pytanie prowokacyjne', description: 'Angażujące pytanie' },
  { id: 'solution', label: 'Problem-rozwiązanie', description: 'Jak rozwiązujemy problem' },
  { id: 'social', label: 'Dowód społeczny', description: 'Inni już korzystają' },
  { id: 'numbers', label: 'Konkretne wyniki', description: 'Liczby i dane' },
  { id: 'urgency', label: 'Pilność', description: 'Ograniczenie czasu' },
];

export const HeadlineAIGenerator = () => {
  const { toast } = useToast();
  
  // Headlines Generator State
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [selectedStrategies, setSelectedStrategies] = useState<string[]>([]);
  const [aiContext, setAiContext] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedHeadlines, setGeneratedHeadlines] = useState<any[]>([]);
  const [selectedHeadlines, setSelectedHeadlines] = useState<Set<number>>(new Set());
  const [editedHeadlines, setEditedHeadlines] = useState<Record<number, any>>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [bulkTags, setBulkTags] = useState("");

  // Meta Ads Copy Generator State
  const [showMetaAdsDialog, setShowMetaAdsDialog] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [campaignLink, setCampaignLink] = useState("");
  const [adStyle, setAdStyle] = useState<'problem-driven' | 'value-driven' | 'proof-driven' | 'cta-heavy'>('problem-driven');
  const [useEmoji, setUseEmoji] = useState(true);
  const [isGeneratingMetaAds, setIsGeneratingMetaAds] = useState(false);
  const [metaAdsCopy, setMetaAdsCopy] = useState<any[]>([]);
  const [regeneratingField, setRegeneratingField] = useState<{index: number, field: string} | null>(null);
  const [metaAdsHistory, setMetaAdsHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Fetch available tags on mount
  const fetchMetaAdsHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('meta_ads_generations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      setMetaAdsHistory(data || []);
    } catch (error) {
      console.error('Error fetching meta ads history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    const fetchTags = async () => {
      const { data } = await supabase
        .from('headline_variants')
        .select('tags')
        .eq('is_active', true);
      
      if (data) {
        const allTags = new Set<string>();
        data.forEach(variant => {
          if (variant.tags) {
            variant.tags.forEach((tag: string) => allTags.add(tag));
          }
        });
        setAvailableTags(Array.from(allTags).sort());
      }
    };
    
    fetchTags();
    fetchMetaAdsHistory();
  }, []);

  const toggleStrategy = (strategyId: string) => {
    setSelectedStrategies(prev =>
      prev.includes(strategyId)
        ? prev.filter(id => id !== strategyId)
        : [...prev, strategyId]
    );
  };

  const handleGenerateWithAI = async () => {
    if (selectedStrategies.length === 0) {
      toast({
        title: "Wybierz strategię",
        description: "Musisz wybrać przynajmniej jedną strategię copywriterską",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-headlines', {
        body: {
          strategies: selectedStrategies,
          context: aiContext
        }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.variants || data.variants.length === 0) {
        throw new Error('Nie otrzymano nagłówków z API');
      }
      
      setGeneratedHeadlines(data.variants);
      const count = data.variants.length;
      toast({
        title: "Wygenerowano nagłówki",
        description: `Wygenerowano ${count} ${count === 1 ? 'wariant' : count < 5 ? 'warianty' : 'wariantów'}`,
      });
    } catch (error: any) {
      console.error('Error generating headlines:', error);
      toast({
        title: "Błąd",
        description: error.message || "Nie udało się wygenerować nagłówków",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleHeadlineSelection = (index: number) => {
    setSelectedHeadlines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleEditHeadline = (index: number, field: string, value: string) => {
    setEditedHeadlines(prev => ({
      ...prev,
      [index]: {
        ...generatedHeadlines[index],
        ...prev[index],
        [field]: value
      }
    }));
  };

  const handleAddSelectedHeadlines = async () => {
    if (selectedHeadlines.size === 0) {
      toast({
        title: "Wybierz nagłówki",
        description: "Musisz wybrać przynajmniej jeden nagłówek do dodania",
        variant: "destructive",
      });
      return;
    }

    const parsedBulkTags = bulkTags
      .split(/[,\s]+/)
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    const headlinesToAdd = Array.from(selectedHeadlines).map(index => {
      const edited = editedHeadlines[index];
      const original = generatedHeadlines[index];
      const headline = edited || original;
      
      return {
        headline_text: headline.headline,
        subtitle: headline.subtitle || null,
        button_text: headline.button_text || "Rozpocznij quiz",
        is_active: true,
        notes: headline.notes || '',
        tags: parsedBulkTags.length > 0 ? parsedBulkTags : (headline.tags || [])
      };
    });

    try {
      for (const headline of headlinesToAdd) {
        const { error } = await supabase
          .from('headline_variants')
          .insert([headline]);
        
        if (error) throw error;
      }
      
      toast({
        title: "Sukces",
        description: `Dodano ${headlinesToAdd.length} nagłówków`,
      });

      // Reset and close
      setShowAIDialog(false);
      setGeneratedHeadlines([]);
      setSelectedHeadlines(new Set());
      setEditedHeadlines({});
      setSelectedStrategies([]);
      setAiContext("");
      setBulkTags("");
      
      // Refresh parent component
      window.location.reload();
    } catch (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się dodać wszystkich nagłówków",
        variant: "destructive",
      });
    }
  };

  const handleGenerateMetaAdsCopy = async () => {
    if (!selectedTag) {
      toast({
        title: "Wybierz tag",
        description: "Musisz wybrać tag kampanii",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingMetaAds(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-meta-ads-copy', {
        body: { tag: selectedTag, campaignLink, adStyle, useEmoji }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      if (!data.variants || data.variants.length === 0) {
        throw new Error('Nie otrzymano copy z API');
      }

      setMetaAdsCopy(data.variants);
      
      // Refresh history
      fetchMetaAdsHistory();
      
      toast({
        title: "Wygenerowano copy",
        description: `Wygenerowano ${data.variants.length} wariantów dla tagu "${selectedTag}"`,
      });
    } catch (error: any) {
      console.error('Error generating meta ads copy:', error);
      toast({
        title: "Błąd",
        description: error.message || "Nie udało się wygenerować copy",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingMetaAds(false);
    }
  };

  const handleRegenerateField = async (index: number, field: 'primary_text' | 'headline' | 'description') => {
    setRegeneratingField({ index, field });
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-meta-ads-copy', {
        body: { tag: selectedTag, campaignLink, adStyle, useEmoji }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Replace just the specific field with a new variant
      const newVariants = [...metaAdsCopy];
      if (data.variants && data.variants[0]) {
        newVariants[index] = {
          ...newVariants[index],
          [field]: data.variants[0][field],
          char_counts: {
            ...newVariants[index].char_counts,
            [field]: data.variants[0][field].length
          }
        };
        setMetaAdsCopy(newVariants);
        
        toast({
          title: "Zregenerowano",
          description: "Pole zostało zaktualizowane nowym copy",
        });
      }
    } catch (error: any) {
      console.error('Error regenerating field:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zregenerować pola",
        variant: "destructive",
      });
    } finally {
      setRegeneratingField(null);
    }
  };

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Skopiowano",
      description: `${fieldName} został skopiowany do schowka`,
    });
  };

  return (
    <>
      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Generowanie wariantów nagłówków</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Użyj AI do wygenerowania różnych wariantów nagłówków dla swoich kampanii
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setShowAIDialog(true)}
            className="gap-2"
          >
            <Sparkles className="h-4 w-4" />
            Generuj z AI
          </Button>
        </div>
      </Card>

      {/* Meta Ads Copy Generator Card */}
      <Card className="p-6 mb-6 border-primary/20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              Generator Copy do Meta Ads
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Generuj profesjonalne copy do reklam Facebook/Instagram na podstawie tagów kampanii
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setShowMetaAdsDialog(true)}
            className="gap-2"
            variant="outline"
          >
            <Sparkles className="h-4 w-4" />
            Generuj Meta Ads
          </Button>
        </div>
      </Card>

      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generowanie nagłówków z AI</DialogTitle>
            <DialogDescription>
              Wybierz strategie copywriterskie i dodaj kontekst, aby wygenerować warianty nagłówków
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Strategy Selection */}
            <div className="space-y-3">
              <Label>Wybierz strategie copywriterskie *</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {STRATEGIES.map(strategy => (
                  <div
                    key={strategy.id}
                    className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedStrategies.includes(strategy.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => toggleStrategy(strategy.id)}
                  >
                    <Checkbox
                      checked={selectedStrategies.includes(strategy.id)}
                      onCheckedChange={() => toggleStrategy(strategy.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{strategy.label}</div>
                      <div className="text-xs text-muted-foreground">{strategy.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Context */}
            <div className="space-y-2">
              <Label htmlFor="ai-context">Dodatkowy kontekst (opcjonalnie)</Label>
              <Textarea
                id="ai-context"
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                placeholder="np. Nasz produkt pomaga firmom B2B automatyzować procesy sprzedaży przez LinkedIn..."
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Dodaj więcej informacji o Twojej firmie, produkcie lub grupie docelowej
              </p>
            </div>

            {/* Generate Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleGenerateWithAI}
                disabled={isGenerating || selectedStrategies.length === 0}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generuję...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generuj nagłówki
                  </>
                )}
              </Button>
            </div>

            {/* Generated Headlines */}
            {generatedHeadlines.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <Label>Wygenerowane nagłówki ({generatedHeadlines.length})</Label>
                  <Button
                    onClick={handleAddSelectedHeadlines}
                    disabled={selectedHeadlines.size === 0}
                    size="sm"
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Dodaj wybrane ({selectedHeadlines.size})
                  </Button>
                </div>

                {/* Bulk Tags Input */}
                {selectedHeadlines.size > 0 && (
                  <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                    <Label htmlFor="bulk-tags" className="text-sm">
                      Tagi dla wybranych wariantów (opcjonalnie)
                    </Label>
                    <Input
                      id="bulk-tags"
                      value={bulkTags}
                      onChange={(e) => setBulkTags(e.target.value)}
                      placeholder="np. kampania-q1, linkedin, premium (oddziel spacją lub przecinkiem)"
                      className="text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Te tagi zostaną dodane do wszystkich wybranych wariantów
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  {generatedHeadlines.map((headline, index) => {
                    const isSelected = selectedHeadlines.has(index);
                    const isEditing = editingIndex === index;
                    const edited = editedHeadlines[index] || headline;

                    return (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border ${
                          isSelected ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleHeadlineSelection(index)}
                            className="mt-1"
                          />
                          
                          <div className="flex-1 space-y-2">
                            {isEditing ? (
                              <>
                                <Input
                                  value={edited.headline}
                                  onChange={(e) => handleEditHeadline(index, 'headline', e.target.value)}
                                  placeholder="Nagłówek"
                                  className="font-medium"
                                />
                                <Textarea
                                  value={edited.subtitle || ''}
                                  onChange={(e) => handleEditHeadline(index, 'subtitle', e.target.value)}
                                  placeholder="Podtytuł"
                                  rows={2}
                                  className="text-sm"
                                />
                                <Input
                                  value={edited.button_text || 'Rozpocznij quiz'}
                                  onChange={(e) => handleEditHeadline(index, 'button_text', e.target.value)}
                                  placeholder="Tekst buttona"
                                  className="text-sm"
                                />
                              </>
                            ) : (
                              <>
                                <div className="font-medium">{edited.headline}</div>
                                {edited.subtitle && (
                                  <div className="text-sm text-muted-foreground">{edited.subtitle}</div>
                                )}
                                <div className="text-xs text-muted-foreground">
                                  Button: {edited.button_text || 'Rozpocznij quiz'}
                                </div>
                                {edited.tags && edited.tags.length > 0 && (
                                  <div className="flex gap-1 flex-wrap">
                                    {edited.tags.map((tag: string) => (
                                      <Badge key={tag} variant="outline" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingIndex(isEditing ? null : index)}
                          >
                            {isEditing ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Meta Ads Copy Generator Dialog */}
      <Dialog open={showMetaAdsDialog} onOpenChange={setShowMetaAdsDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generator Copy do Meta Ads</DialogTitle>
            <DialogDescription>
              Wybierz tag kampanii i wygeneruj 5 wersji copy zgodnych z limitami Meta Ads
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Ad Style & Emoji Selection */}
            <div className="space-y-4">
              <div>
                <Label>Styl reklamy *</Label>
                <RadioGroup value={adStyle} onValueChange={(value) => setAdStyle(value as any)} className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="problem-driven" id="problem" />
                    <Label htmlFor="problem" className="font-normal cursor-pointer">
                      <span className="font-medium">Problem-driven</span> - zaczynają się od bólu klienta
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="value-driven" id="value" />
                    <Label htmlFor="value" className="font-normal cursor-pointer">
                      <span className="font-medium">Value-driven</span> - pokazują korzyści i rezultaty
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="proof-driven" id="proof" />
                    <Label htmlFor="proof" className="font-normal cursor-pointer">
                      <span className="font-medium">Proof-driven</span> - liczby, referencje, case studies
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="cta-heavy" id="cta" />
                    <Label htmlFor="cta" className="font-normal cursor-pointer">
                      <span className="font-medium">CTA-heavy</span> - bezpośrednie wezwania do działania
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border">
                <Label htmlFor="emoji-toggle" className="cursor-pointer">
                  Używaj emoji 🚀📈✅
                </Label>
                <Switch 
                  id="emoji-toggle"
                  checked={useEmoji} 
                  onCheckedChange={setUseEmoji}
                />
              </div>
            </div>

            {/* Tag Selection */}
            <div className="space-y-2">
              <Label htmlFor="tag-select">Tag kampanii *</Label>
              <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger id="tag-select">
                  <SelectValue placeholder="Wybierz tag..." />
                </SelectTrigger>
                <SelectContent>
                  {availableTags.length === 0 ? (
                    <SelectItem value="_empty" disabled>
                      Brak dostępnych tagów
                    </SelectItem>
                  ) : (
                    availableTags.map(tag => (
                      <SelectItem key={tag} value={tag}>
                        {tag}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                AI przeanalizuje aktywne warianty nagłówków z tym tagiem
              </p>
            </div>

            {/* Campaign Link */}
            <div className="space-y-2">
              <Label htmlFor="campaign-link">Link kampanii (opcjonalnie)</Label>
              <Input
                id="campaign-link"
                value={campaignLink}
                onChange={(e) => setCampaignLink(e.target.value)}
                placeholder="https://..."
                type="url"
              />
              <p className="text-xs text-muted-foreground">
                Dodatkowy kontekst dla AI - np. link do landing page
              </p>
            </div>

            {/* Generate Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleGenerateMetaAdsCopy}
                disabled={isGeneratingMetaAds || !selectedTag}
              >
                {isGeneratingMetaAds ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generuję...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generuj Meta Ads Copy
                  </>
                )}
              </Button>
            </div>

            {/* History Section */}
            {metaAdsHistory.length > 0 && (
              <div className="rounded-lg border p-4 space-y-3">
                <Label className="text-sm font-medium">Ostatnie generacje ({metaAdsHistory.length})</Label>
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {metaAdsHistory.map((generation) => (
                      <Card key={generation.id} className="p-3 hover:bg-accent/50 cursor-pointer transition-colors"
                            onClick={() => {
                              setMetaAdsCopy(generation.generated_variants);
                              setSelectedTag(generation.tag);
                              setCampaignLink(generation.campaign_link || '');
                              toast({
                                title: "Załadowano z historii",
                                description: `Przywrócono generację z ${new Date(generation.created_at).toLocaleString('pl-PL')}`,
                              });
                            }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{generation.tag}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(generation.created_at).toLocaleDateString('pl-PL', { 
                                day: '2-digit', 
                                month: 'short', 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {generation.generated_variants?.length || 0} wariantów
                          </span>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Character Limits Info */}
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <h4 className="font-medium text-sm mb-2">
                Limity znaków Meta Ads:
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Primary Text:</span>
                  <span className="ml-2 text-muted-foreground">
                    150 zal. (maks. 500)
                  </span>
                </div>
                <div>
                  <span className="font-medium">Headline:</span>
                  <span className="ml-2 text-muted-foreground">30 zal. (maks. 40)</span>
                </div>
                <div>
                  <span className="font-medium">Description:</span>
                  <span className="ml-2 text-muted-foreground">
                    300-600 zal. (maks. 1000)
                  </span>
                </div>
              </div>
            </div>

            {/* Generated Meta Ads Copy */}
            {metaAdsCopy.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <Label>Wygenerowane warianty ({metaAdsCopy.length})</Label>
                  <Badge variant="outline">Tag: {selectedTag}</Badge>
                </div>

                <div className="space-y-6">
                  {metaAdsCopy.map((variant, index) => (
                    <Card key={index} className="p-4 space-y-4">
                      <div className="flex items-center justify-between border-b pb-2">
                        <h4 className="font-semibold">Wariant {index + 1}</h4>
                      </div>

                      {/* Primary Text */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">
                            Primary Text ({variant.char_counts?.primary_text || variant.primary_text.length}/500)
                          </Label>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(variant.primary_text, 'Primary Text')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRegenerateField(index, 'primary_text')}
                              disabled={regeneratingField?.index === index && regeneratingField?.field === 'primary_text'}
                            >
                              {regeneratingField?.index === index && regeneratingField?.field === 'primary_text' ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <div className="p-3 bg-muted rounded-md text-sm">
                          {variant.primary_text}
                        </div>
                      </div>

                      {/* Headline */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">
                            Headline ({variant.char_counts?.headline || variant.headline.length}/40)
                          </Label>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(variant.headline, 'Headline')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRegenerateField(index, 'headline')}
                              disabled={regeneratingField?.index === index && regeneratingField?.field === 'headline'}
                            >
                              {regeneratingField?.index === index && regeneratingField?.field === 'headline' ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <div className="p-3 bg-muted rounded-md text-sm font-medium">
                          {variant.headline}
                        </div>
                      </div>

                      {/* Description */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">
                            Description ({variant.char_counts?.description || variant.description.length}/1000)
                          </Label>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(variant.description, 'Description')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRegenerateField(index, 'description')}
                              disabled={regeneratingField?.index === index && regeneratingField?.field === 'description'}
                            >
                              {regeneratingField?.index === index && regeneratingField?.field === 'description' ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <div className="p-3 bg-muted rounded-md text-sm">
                          {variant.description}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="flex justify-center pt-4">
                  <Button
                    variant="outline"
                    onClick={handleGenerateMetaAdsCopy}
                    disabled={isGeneratingMetaAds}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Wygeneruj nowe warianty
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
